'use client'

import { useEffect } from 'react'
import Script from 'next/script'
import { usePathname } from 'next/navigation'
import { IS_NON_PRODUCTION_BUILD } from '@/lib/analytics/non-production-build'
import { isPrivatePath } from '@/lib/analytics/private-paths'
import { GA_SUPPRESS_JS } from '@/lib/analytics/ga-suppression'
import { getStoredConsent, hasAnalyticsConsent, hasMarketingConsent } from './CookieConsentBanner'
import { consentModeDefaultJs, googleTagBlockedJs } from '@/lib/analytics/consent-defaults'
import { gpcFromNavigator } from '@/lib/identity/consent'

const GA4_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID?.trim()
const GTM_ID = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID?.trim()
// AdSense is NOT loaded here any more (UXLIVE-5, visibility audit 2026-09-22).
// Loading adsbygoogle.js on every route ran Auto-ads auctions on every
// brokerage page (/, listings, place pages): all four live probes came back
// unfilled, so it earned nothing while adding third-party weight to pages that
// are meant to rank and convert, and a fill would have put Google-chosen ads,
// portals and competing brokerages included, beside our own listings.
// components/AdUnit.tsx loads the script itself, only where an explicit slot
// renders (the non-transactional tools pages) and only with marketing consent.
// INT-036 in docs/plans/ENTERPRISE_MAP/matrix/INTEGRATIONS.md records the ruling.
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID?.trim()

/**
 * Loads Google's gtag stack with **Consent Mode v2** wired correctly.
 *
 * The previous version of this file only loaded gtag.js after the user
 * accepted the cookie banner. That kept the page cookie-clean for
 * visitors who declined, but it had two real downsides:
 *
 *   1. **Google Signals never worked.** Signals requires gtag to be
 *      loaded so it can communicate consent state. Visitors who never
 *      clicked Accept were invisible to Google entirely.
 *   2. **Cookieless modeling was disabled.** With Consent Mode v2, Google
 *      can still model conversions from visitors who decline cookies, as
 *      long as it knows their consent state. Suppressing gtag entirely
 *      threw away that signal.
 *
 * Consent Mode v2 pattern (now wired here):
 *
 *   1. **Before gtag.js loads** — push Consent Mode v2 region defaults
 *      (analytics granted outside EEA/UK/CH, ad_* denied everywhere;
 *      GPC and a stored decline deny analytics). `wait_for_update: 500ms`
 *      gives the CookieConsentBanner a brief moment to read its cookie
 *      and update the consent state before any tracking pings fire.
 *   2. **Always load gtag.js + GA4 config.** Data collection respects
 *      the consent state Google now knows about.
 *   3. **On consent change** — call `gtag('consent', 'update', ...)`
 *      with `granted` for the categories the user enabled. Persists for
 *      subsequent pageviews via the consent cookie.
 *
 * Best-practice reference:
 *   - https://developers.google.com/tag-platform/security/guides/consent
 *   - https://support.google.com/analytics/answer/14998558 (Consent Mode v2)
 *   - https://support.google.com/analytics/answer/9445345 (Google Signals)
 */
export default function GoogleAnalytics() {
  const pathname = usePathname()
  // Apply current consent state to gtag the moment the script is ready,
  // and whenever the user updates their preferences. Returns the cleanup.
  useEffect(() => {
    function applyConsent() {
      if (typeof window === 'undefined') return
      const w = window as Window & { gtag?: (...args: unknown[]) => void }
      if (typeof w.gtag !== 'function') return
      const gpc = gpcFromNavigator(typeof navigator !== 'undefined' ? navigator : undefined)
      // No stored answer and no GPC: leave the region-specific defaults the
      // bootstrap (or this block's own default) already pushed. An update to
      // denied would wipe analytics_storage granted for US visitors.
      if (!gpc && getStoredConsent() === null) return
      const analytics = hasAnalyticsConsent()
      const marketing = hasMarketingConsent()
      w.gtag('consent', 'update', {
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
        analytics_storage: analytics ? 'granted' : 'denied',
      })
    }

    // Run once on mount in case consent was already stored from a prior visit.
    applyConsent()
    window.addEventListener('cookie-consent', applyConsent)
    return () => window.removeEventListener('cookie-consent', applyConsent)
  }, [])

  const hasGTM = !!GTM_ID
  // GTM-WV6R4NZ5 already ships a GA4 Configuration tag (gaawc). A second
  // gtag('config', G-ST40W4WM6T) doubles page_view and inflates Unassigned
  // sessions. When GTM is present, consent defaults still run; GA4 config does not.
  const hasGA4 = !!GA4_ID && !hasGTM
  const hasGoogleAds = !!GOOGLE_ADS_ID
  if (!hasGA4 && !hasGoogleAds && !hasGTM) return null
  // Never load a Google tag from a dev server or a preview deploy.
  if (IS_NON_PRODUCTION_BUILD) return null
  // A signing link's address is its key: no tag ever sees it (private-paths.ts).
  if (isPrivatePath(pathname)) return null

  const gtagScriptId = hasGA4 ? GA4_ID! : (!hasGTM && hasGoogleAds ? GOOGLE_ADS_ID! : null)

  return (
    <>
      {/* 1. Consent Mode v2 DEFAULTS — must inject before gtag.js so the
              first tracking ping carries the correct consent state. Uses
              `beforeInteractive` so it runs synchronously before any other
              tracking script gets to fire its initial event.
              With GTM (production), the ONE default is the GTM bootstrap's
              (lib/analytics/gtm-bootstrap.ts), pushed before gtm.js. This
              block's own default ran after gtm.js had loaded, a second, late
              default Google ignores at best (GA4 audit 2026-10-08, plan B6),
              so it only renders on a build with no GTM container, where it
              is the only default. Both paths use the same shared region
              defaults (lib/analytics/consent-defaults.ts, settled in #437,
              2026-10-08), so they hold the same values. */}
      <Script id="gtag-consent-defaults" strategy="beforeInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date()); // hydration-safe — injected gtag bootstrap, not React render clock
          // Consent Mode v2 defaults (lib/analytics/consent-defaults.ts): region-scoped
          // denied for EEA/UK/CH, analytics granted elsewhere, ad_* denied everywhere.
          // GPC and a stored decline deny analytics before any tag. wait_for_update
          // holds the first ping while a stored accept is applied. With GTM, the GTM
          // bootstrap sets the same defaults before gtm.js, so this block skips them.
          ${hasGTM ? `// Consent defaults: the GTM bootstrap (lib/analytics/gtm-bootstrap.ts) sets them before gtm.js loads.` : consentModeDefaultJs()}
          // URL-passthrough: when consent is denied, GA4 still propagates
          // gclid/dclid/utm_* across navigation via the URL instead of a
          // cookie. Keeps attribution intact for cookieless visitors.
          gtag('set', 'url_passthrough', true);
          // Ads data redaction: when consent is denied, redact PII from
          // the data sent to Google Ads. Required for Consent Mode v2
          // compliance.
          gtag('set', 'ads_data_redaction', true);
          // First-paint broker user property so GTM/gtag page_view is not
          // Direct-only when the visitor arrived on ?agent= / cookie.
          (function () {
            try {
              var agent = new URLSearchParams(window.location.search || '').get('agent');
              if (!agent) {
                var m = document.cookie.match(/(?:^|; )rr_agent_attribution=([^;]*)/);
                if (m) {
                  try {
                    var parsed = JSON.parse(decodeURIComponent(m[1]));
                    agent = parsed && parsed.slug;
                  } catch (e) {
                    agent = decodeURIComponent(m[1]);
                  }
                }
              }
              if (!agent) return;
              agent = String(agent).trim().toLowerCase();
              var map = { matt: 'matt', 'matt-ryan': 'matt', rebecca: 'rebecca', 'rebecca-peterson': 'rebecca', paul: 'paul', 'paul-stevenson': 'paul' };
              var slug = map[agent];
              if (!slug) return;
              gtag('set', 'user_properties', { assigned_broker: slug });
              window.dataLayer.push({ broker_slug: slug, assigned_broker: slug });
            } catch (e) {}
          })();
        `}
      </Script>

      {/* 2. gtag.js + GA4 config — loaded always now, since consent state
              is communicated via the defaults block above. Cookieless
              modeling kicks in automatically when consent is denied.
              Never on a page GA4 must not count (lib/analytics/ga-suppression.ts,
              Matt 2026-10-05): the decision runs in the browser BEFORE gtag.js
              is requested, so /admin, a non-production host, automation and a
              signed-in broker's browser load no Google tag at all. Nor
              after a stored decline or under GPC (G3, googleTagBlockedJs). */}
      {(hasGA4 || hasGoogleAds) && gtagScriptId && (
        <>
          <Script id="ga4-gads-config" strategy="afterInteractive">
            {`
              if (!${googleTagBlockedJs()}) if (!${GA_SUPPRESS_JS}) {
              (function() {
                var tag = document.createElement('script');
                tag.async = true;
                tag.src = 'https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gtagScriptId)}';
                document.head.appendChild(tag);
              })();
              (function() {
                var params = new URLSearchParams(window.location.search || '');
                var utmSource = params.get('utm_source');
                var inferredSource = null;
                var inferredMedium = null;

                if (!utmSource) {
                  if (params.get('fbclid')) {
                    inferredSource = 'facebook';
                    inferredMedium = 'paid_social';
                  } else if (params.get('ttclid')) {
                    inferredSource = 'tiktok';
                    inferredMedium = 'paid_social';
                  } else if (params.get('gclid')) {
                    inferredSource = 'google';
                    inferredMedium = 'cpc';
                  } else if (params.get('msclkid')) {
                    inferredSource = 'bing';
                    inferredMedium = 'cpc';
                  }
                }

                var gaConfig = {
                  // Cross-domain linker: keep client_id stable when a
                  // visitor hops between the apex and its subdomains.
                  // Prevents the same person from showing as two sessions.
                  linker: {
                    domains: ['ryan-realty.com', 'www.ryan-realty.com', 'seller.ryan-realty.com', 'buyer.ryan-realty.com'],
                    accept_incoming: true
                  }
                };
                if (inferredSource && inferredMedium) {
                  gaConfig.campaign_source = inferredSource;
                  gaConfig.campaign_medium = inferredMedium;
                  gaConfig.campaign_name = params.get('utm_campaign') || 'auto-click-id';
                }

                ${hasGA4 ? `gtag('config', '${GA4_ID!.replace(/'/g, "\\'")}', gaConfig);` : ''}
              })();
              ${hasGoogleAds ? `gtag('config', '${GOOGLE_ADS_ID!.replace(/'/g, "\\'")}');` : ''}
              }
            `}
          </Script>
        </>
      )}
    </>
  )
}
